/**
 * Offres de lot Moon Bundles.
 *
 * Moon Bundles ne publie rien dans l'API Storefront : ses offres ne vivent que
 * dans le HTML de la page produit du site marchand, sur un autre domaine, qui
 * n'autorise aucune origine tierce. La boutique virtuelle lit donc un
 * instantané, `public/offers.json`, produit hors ligne par
 * `tools/extraire-offres-moonbundle.py`.
 *
 * Conséquence à connaître : une offre modifiée dans Moon Bundles n'apparaît ici
 * qu'après avoir rejoué ce script et redéployé. Sans instantané, la fiche
 * retombe simplement sur ses déclinaisons, elle ne casse pas.
 */

export interface BundleOffer {
  /** Nombre d'exemplaires du produit dans ce lot. */
  quantite: number;
  titre: string;
  sousTitre: string;
  /** `none` ou `percent` : la seule remise utilisée par la boutique. */
  remise: string;
  valeurRemise: number | null;
  misEnAvant: boolean;
  mentionMiseEnAvant: string;
}

let offres: Record<string, BundleOffer[]> | null = null;
let chargement: Promise<void> | null = null;

export async function loadOffers(): Promise<void> {
  if (offres !== null) return;
  if (!chargement) {
    chargement = fetch('/offers.json')
      .then((reponse) => (reponse.ok ? reponse.json() : {}))
      .then((donnees: unknown) => {
        offres = typeof donnees === 'object' && donnees !== null
          ? (donnees as Record<string, BundleOffer[]>)
          : {};
      })
      .catch(() => {
        // Pas d'instantané : la fiche se contentera des déclinaisons.
        offres = {};
      });
  }
  await chargement;
}

/**
 * Offres d'un produit, y compris lorsqu'il n'y en a qu'une : un palier unique
 * ne propose aucun choix, mais il peut porter un prix négocié différent du prix
 * Shopify, et l'ignorer afficherait un tarif plus cher que celui du site.
 */
export function offersFor(handle: string): BundleOffer[] {
  const lot = offres?.[handle];
  return Array.isArray(lot) ? lot : [];
}

/**
 * Prix d'un lot, remise comprise. Deux formes existent dans Moon Bundles :
 *
 * - `percent` : un pourcentage retiré du prix plein du lot.
 * - `specific` : un prix TOTAL imposé pour le lot, quel que soit le prix
 *   unitaire. C'est la forme utilisée par le pack géant et la queue de bœuf
 *   au kilo.
 *
 * Toute autre forme est traitée comme une absence de remise : mieux vaut un
 * prix plein exact qu'une remise devinée.
 */
export function bundlePrice(offre: BundleOffer, prixUnitaire: number): number {
  const plein = prixUnitaire * offre.quantite;
  if (typeof offre.valeurRemise !== 'number') return plein;
  if (offre.remise === 'percent') return plein * (1 - offre.valeurRemise / 100);
  if (offre.remise === 'specific') return offre.valeurRemise;
  return plein;
}

/** Prix plein du lot, à barrer quand une remise s'applique. */
export function bundleCompareAt(offre: BundleOffer, prixUnitaire: number): number | null {
  const plein = prixUnitaire * offre.quantite;
  const paye = bundlePrice(offre, prixUnitaire);
  return paye < plein - 0.005 ? plein : null;
}
